import { useNavigate } from "react-router-dom";
import { Button, Modal, Typography } from "@/components/atoms";
import { useOperator } from "@/hooks";

interface Props {
  open: boolean;
  onClose: () => void;
  valid: number;
  total: number;
}

export const VehicleTypesSetupModal = ({ open, onClose, valid, total }: Props) => {
  const navigate = useNavigate();
  const { id: operatorId } = useOperator();

  const goToVehicleTypes = () => {
    onClose();
    navigate(`/operator/${operatorId}/config/trip/vehicle-types`);
  };

  return (
    <Modal open={open} onClose={onClose} variant="web-small" className="!p-0 [&>div]:!gap-0">
      <div className="flex flex-col">
        <div className="flex flex-col gap-1 bg-neutral-surface-gray px-5 pb-2.5 pt-5">
          <Typography className="text-lg font-semibold leading-5 text-neutral-black">Set up vehicle types first</Typography>
          <Typography className="text-base leading-5 text-neutral-dark-gray">
            Before adding vehicles, please update your vehicle types in Settings. Each type now requires a vehicle category mapping.
          </Typography>
        </div>
        <div className="flex flex-col gap-4 px-5 py-5">
          <div className="pb-4">
            <Step
              index={1}
              title="Go to Vehicle Types"
              body={
                <>
                  <Typography className="text-base leading-5 text-neutral-dark-gray">
                    Update each vehicle type by selecting the correct vehicle category. This ensures vehicles are matched properly,
                    especially for offloaded jobs.
                  </Typography>
                  <Typography className="mt-5 text-base leading-5 text-neutral-dark-gray">
                    <span className="font-semibold">Vehicle Types</span> now act as your main classification (previously{" "}
                    <span className="font-semibold">Vehicle Categories</span>), with an added mapping layer to improve matching accuracy.
                  </Typography>
                  <Typography className="mt-5 text-base leading-5 text-neutral-dark-gray">
                    Go to <span className="text-info underline">Settings</span> →{" "}
                    <span className="text-info underline">Trip Settings</span> →{" "}
                    <span className="text-info underline">Transfer & Vehicle Options</span> →{" "}
                    <span className="text-info underline">Vehicle Types</span>
                  </Typography>
                </>
              }
            />
          </div>
          <div className="pb-2">
            <Step
              index={2}
              title="Come back to Vehicles"
              body={
                <Typography className="text-base leading-5 text-neutral-dark-gray">
                  Once done, you can start adding and assigning vehicles as usual, with correct matching across both internal and
                  offloaded jobs.
                </Typography>
              }
            />
          </div>
          <div className="flex flex-col gap-2 rounded-lg border border-danger bg-danger-light p-2.5">
            <Typography className="text-base font-semibold leading-5 text-danger-dark">Can&apos;t Add a Vehicle Yet</Typography>
            <Typography className="text-base leading-5 text-danger-dark">
              You need to map a category to all vehicle types before adding vehicles.
            </Typography>
            <Typography className="mt-5 text-base leading-5 text-danger-dark">
              ({valid}/{total}) Vehicle Type Updated
            </Typography>
          </div>
        </div>
        <div className="flex justify-end px-5 pb-6">
          <Button variant="primary" onClick={goToVehicleTypes}>
            Go to Vehicle Types
          </Button>
        </div>
      </div>
    </Modal>
  );
};

interface StepProps {
  index: number;
  title: string;
  body: React.ReactNode;
}

const Step = ({ index, title, body }: StepProps) => (
  <div className="flex items-start gap-4">
    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-vivid-purple/10">
      <Typography className="text-lg font-semibold leading-5 text-vivid-purple-dark">{index}</Typography>
    </div>
    <div className="flex flex-1 flex-col gap-1">
      <Typography className="text-base font-semibold leading-5 text-neutral-black">{title}</Typography>
      {body}
    </div>
  </div>
);
